import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CardLayoutPreview.module.scss';

const CORNERS = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];
const CARD_ASPECT = 1.7646768867;

const SIZE_LIMITS = {
    text: { min: 2, max: 10, default: 3.7 },
    title: { min: 2, max: 10, default: 7 },
    photo: { min: 8, max: 40, default: 18 },
    logo: { min: 8, max: 40, default: 18 },
};

function clampSize(type, value) {
    const limits = SIZE_LIMITS[type] || { min: 1, max: 100, default: 10 };
    const n = Number(value);
    if (!Number.isFinite(n)) return limits.default;
    return Math.min(limits.max, Math.max(limits.min, Math.round(n * 10) / 10));
}

function nearestCorner(xRatio, yRatio) {
    const left = xRatio < 0.5;
    const top = yRatio < 0.5;
    if (top && left) return 'top_left';
    if (top && !left) return 'top_right';
    if (!top && left) return 'bottom_left';
    return 'bottom_right';
}

/** Member text: fixed card-width insets. Title X scales with font; title Y is tighter to the edge. */
const MEMBER_TEXT_INSET_X = 5;
const MEMBER_TEXT_INSET_Y = 5;
const TITLE_INSET_X_PER_SIZE = 3.2 / 3.7;
const TITLE_INSET_Y_PER_SIZE = 2.4 / 3.7;
const MEMBER_TEXT_SIZE = 3.7;
const MEDIA_INSET_X_CQI = 5;
const MEDIA_INSET_Y_CQI = 5.5;
const SUBTITLE_SIZE_RATIO = 0.62;

/** Positions use cqi (card width) — same basis as the app's scale(), not CSS % of height. */
function cornerPositionStyle(location, insetXCqi, insetYCqi) {
    const insetX = `${insetXCqi}cqi`;
    const insetY = `${insetYCqi}cqi`;
    switch (location) {
        case 'top_left':
            return { top: insetY, left: insetX, right: 'auto', bottom: 'auto' };
        case 'top_right':
            return { top: insetY, right: insetX, left: 'auto', bottom: 'auto' };
        case 'bottom_left':
            return { bottom: insetY, left: insetX, top: 'auto', right: 'auto' };
        case 'bottom_right':
            return { bottom: insetY, right: insetX, top: 'auto', left: 'auto' };
        default:
            return { bottom: insetY, left: insetX, top: 'auto', right: 'auto' };
    }
}

function insetsForMemberText() {
    return {
        x: MEMBER_TEXT_INSET_X,
        y: MEMBER_TEXT_INSET_Y,
    };
}

function resizeHandleAnchor(location) {
    if (location === 'top_right') return 'sw';
    if (location === 'bottom_left') return 'ne';
    if (location === 'bottom_right') return 'nw';
    return 'se';
}

function insetsForTitle(sizePct) {
    return {
        x: sizePct * TITLE_INSET_X_PER_SIZE,
        y: sizePct * TITLE_INSET_Y_PER_SIZE,
    };
}

/**
 * Interactive membership-card layout preview.
 * Drag to move; drag the inner-corner handle to resize (sizes are % of card width).
 */
export default function CardLayoutPreview({
    cardImage,
    cardColour,
    fontColour,
    logo,
    cardHeading,
    cardSubtitle,
    textLocation = 'bottom_left',
    photoLocation = 'top_right',
    titleLocation = 'top_left',
    logoLocation = 'bottom_right',
    titleSize = 7,
    photoSize = 18,
    logoSize = 18,
    showPhoto = false,
    showTitle = false,
    showLogo = false,
    /** When true, show solid colour even if cardImage is set (colour tab selected). */
    preferColour = false,
    onTextLocationChange,
    onPhotoLocationChange,
    onTitleLocationChange,
    onLogoLocationChange,
    onTitleSizeChange,
    onPhotoSizeChange,
    onLogoSizeChange,
    onResetDefaults,
    disabled = false,
}) {
    const { t } = useTranslation();
    const cardRef = useRef(null);
    const dragRef = useRef(null);
    const [drag, setDrag] = useState(null);
    const [liveSizes, setLiveSizes] = useState(null);

    const sizes = {
        text: MEMBER_TEXT_SIZE,
        title: liveSizes?.title ?? titleSize,
        photo: liveSizes?.photo ?? photoSize,
        logo: liveSizes?.logo ?? logoSize,
    };

    const useColour = preferColour || !cardImage;
    const bgStyle = useColour
        ? {
              backgroundColor: cardColour || '#de2c49',
              backgroundImage: 'none',
          }
        : {
              backgroundImage: `url(${cardImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
          };

    const textColor = fontColour || '#ffffff';
    const heading =
        String(cardHeading || '')
            .replace(/\u00AD/g, '')
            .replace(/¬/g, '')
            .trim() || t('group.preview_card_heading');
    const subtitle = String(cardSubtitle || '').trim();
    const titleCqi = sizes.title;
    const subtitleCqi = titleCqi * SUBTITLE_SIZE_RATIO;

    const placements = {
        text: textLocation,
        photo: showPhoto ? photoLocation : null,
        title: showTitle ? titleLocation : null,
        logo: showLogo ? logoLocation : null,
    };

    const occupantAt = useCallback(
        (corner, exceptType) =>
            Object.entries(placements).find(
                ([key, location]) => key !== exceptType && location && location === corner
            )?.[0] || null,
        [placements]
    );

    const emitLocation = useCallback(
        (type, corner) => {
            if (type === 'text') onTextLocationChange?.(corner);
            if (type === 'photo') onPhotoLocationChange?.(corner);
            if (type === 'title') onTitleLocationChange?.(corner);
            if (type === 'logo') onLogoLocationChange?.(corner);
        },
        [
            onLogoLocationChange,
            onPhotoLocationChange,
            onTextLocationChange,
            onTitleLocationChange,
        ]
    );

    const emitSize = useCallback(
        (type, value) => {
            const next = clampSize(type, value);
            if (type === 'title') onTitleSizeChange?.(next);
            if (type === 'photo') onPhotoSizeChange?.(next);
            if (type === 'logo') onLogoSizeChange?.(next);
        },
        [onLogoSizeChange, onPhotoSizeChange, onTitleSizeChange]
    );

    const commitDrop = useCallback(
        (type, clientX, clientY) => {
            const card = cardRef.current;
            if (!card) return;
            const rect = card.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return;

            const xRatio = (clientX - rect.left) / rect.width;
            const yRatio = (clientY - rect.top) / rect.height;
            const corner = nearestCorner(
                Math.min(1, Math.max(0, xRatio)),
                Math.min(1, Math.max(0, yRatio))
            );

            const from = placements[type];
            if (!from || corner === from) return;

            const occupant = occupantAt(corner, type);
            if (occupant) {
                emitLocation(occupant, from);
            }
            emitLocation(type, corner);
        },
        [emitLocation, occupantAt, placements]
    );

    useEffect(() => {
        dragRef.current = drag;
    }, [drag]);

    useEffect(() => {
        if (!drag) return undefined;

        const onMove = (event) => {
            const card = cardRef.current;
            const prev = dragRef.current;
            if (!card || !prev) return;
            const rect = card.getBoundingClientRect();

            if (prev.mode === 'resize') {
                const deltaPx = event.clientX - prev.startX;
                const signed = prev.invertX ? -deltaPx : deltaPx;
                const deltaPct = (signed / rect.width) * 100;
                const nextSize = clampSize(prev.type, prev.startSize + deltaPct);
                setLiveSizes((s) => ({ ...(s || {}), [prev.type]: nextSize }));
                setDrag({ ...prev, currentSize: nextSize });
                return;
            }

            setDrag({
                ...prev,
                left: event.clientX - rect.left - prev.offsetX,
                top: event.clientY - rect.top - prev.offsetY,
            });
        };

        const onUp = (event) => {
            const prev = dragRef.current;
            if (prev?.mode === 'resize') {
                emitSize(prev.type, prev.currentSize ?? prev.startSize);
                setLiveSizes(null);
            } else if (prev?.mode === 'move') {
                commitDrop(prev.type, event.clientX, event.clientY);
            }
            setDrag(null);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, [!!drag, commitDrop, emitSize]);

    const boxForType = (type, cardWidth) => {
        if (type === 'text') {
            const font = (sizes.text / 100) * cardWidth;
            return { width: cardWidth * 0.65, height: Math.max(font * 2.6, cardWidth * 0.08) };
        }
        if (type === 'title') {
            const font = (titleCqi / 100) * cardWidth;
            const extra = subtitle ? (subtitleCqi / 100) * cardWidth * 1.35 : 0;
            return {
                width: font * 12,
                height: Math.max(font * 1.4 + extra, cardWidth * 0.045),
            };
        }
        if (type === 'photo') {
            // App: height = size% of card width, width = height * 7/9.
            const height = (sizes.photo / 100) * cardWidth;
            return { width: height * (7 / 9), height };
        }
        const width = (sizes.logo / 100) * cardWidth;
        return { width, height: width };
    };

    const startMove = (type, event) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();
        const card = cardRef.current;
        if (!card) return;
        const elRect = event.currentTarget.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        const box =
            type === 'logo' || type === 'title'
                ? { width: elRect.width, height: elRect.height }
                : boxForType(type, cardRect.width);
        const location =
            type === 'text'
                ? textLocation
                : type === 'title'
                  ? titleLocation
                  : type === 'logo'
                    ? logoLocation
                    : photoLocation;
        const onRight = location === 'top_right' || location === 'bottom_right';
        const onBottom = location === 'bottom_left' || location === 'bottom_right';

        // Keep the visible edge anchored when expanding shrink-wrapped text to the full box.
        let left = onRight
            ? elRect.right - cardRect.left - box.width
            : elRect.left - cardRect.left;
        let top = onBottom
            ? elRect.bottom - cardRect.top - box.height
            : elRect.top - cardRect.top;

        setDrag({
            mode: 'move',
            type,
            left,
            top,
            offsetX: event.clientX - cardRect.left - left,
            offsetY: event.clientY - cardRect.top - top,
            boxWidth: box.width,
            boxHeight: box.height,
        });
    };

    const startResize = (type, event, location) => {
        if (disabled || type === 'text') return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const startSize = clampSize(type, sizes[type]);
        setLiveSizes({ [type]: startSize });
        setDrag({
            mode: 'resize',
            type,
            startX: event.clientX,
            startY: event.clientY,
            startSize,
            currentSize: startSize,
            invertX: location === 'top_right' || location === 'bottom_right',
        });
    };

    const insetsForDragType = (type) => {
        if (type === 'title') return insetsForTitle(sizes.title);
        if (type === 'text') return insetsForMemberText();
        return { x: MEDIA_INSET_X_CQI, y: MEDIA_INSET_Y_CQI };
    };

    const hoverCorner =
        drag?.mode === 'move' && cardRef.current
            ? (() => {
                  const rect = cardRef.current.getBoundingClientRect();
                  const centerX = drag.left + (drag.boxWidth || 40) / 2;
                  const centerY = drag.top + (drag.boxHeight || 40) / 2;
                  return nearestCorner(
                      Math.min(1, Math.max(0, centerX / rect.width)),
                      Math.min(1, Math.max(0, centerY / rect.height))
                  );
              })()
            : null;

    const snapCorner = drag?.mode === 'move' && hoverCorner ? hoverCorner : null;
    const swapWith =
        drag?.mode === 'move' && hoverCorner
            ? occupantAt(hoverCorner, drag.type)
            : null;

    const liveLocation = (type) => {
        if (drag?.mode !== 'move') return placements[type];
        if (type === drag.type) return hoverCorner || placements[type];
        if (swapWith && type === swapWith) return placements[drag.type];
        return placements[type];
    };

    const liveTitleLocation = liveLocation('title') || titleLocation;
    const liveTextLocation = liveLocation('text') || textLocation;
    const liveLogoLocation = liveLocation('logo') || logoLocation;
    const livePhotoLocation = liveLocation('photo') || photoLocation;

    const moveStyle =
        drag?.mode === 'move'
            ? snapCorner
                ? {
                      ...cornerPositionStyle(
                          snapCorner,
                          insetsForDragType(drag.type).x,
                          insetsForDragType(drag.type).y
                      ),
                      zIndex: 5,
                      width: drag.boxWidth,
                      height: drag.boxHeight,
                      maxWidth: drag.boxWidth,
                  }
                : {
                      left: drag.left,
                      top: drag.top,
                      right: 'auto',
                      bottom: 'auto',
                      zIndex: 5,
                      width: drag.boxWidth,
                      height: drag.boxHeight,
                      maxWidth: drag.boxWidth,
                  }
            : undefined;

    const dropZoneStyle =
        drag?.mode === 'move' && drag.boxWidth
            ? { width: drag.boxWidth, height: drag.boxHeight, minHeight: drag.boxHeight }
            : undefined;

    const dragDropInsets =
        drag?.mode === 'move' ? insetsForDragType(drag.type) : null;

    const RESIZE_ANCHOR_CLASS = {
        se: styles.resizeSE,
        sw: styles.resizeSW,
        ne: styles.resizeNE,
        nw: styles.resizeNW,
    };

    const resizeHandle = (type, location) => (
        <span
            className={`${styles.resizeHandle} ${
                RESIZE_ANCHOR_CLASS[resizeHandleAnchor(location)]
            }`}
            onPointerDown={(e) => startResize(type, e, location)}
            title={t('group.resize_element')}
            aria-label={t('group.resize_element')}
        />
    );

    const titleAlignCorner =
        drag?.mode === 'move' && drag?.type === 'title'
            ? snapCorner || liveTitleLocation
            : liveTitleLocation;
    const textAlignCorner =
        drag?.mode === 'move' && drag?.type === 'text'
            ? snapCorner || liveTextLocation
            : liveTextLocation;

    return (
        <div className={styles.wrap}>
            <div className={styles.toolbar}>
                <p className={styles.hint}>{t('group.drag_resize_layout_hint')}</p>
                {onResetDefaults && (
                    <button
                        type="button"
                        className={styles.resetButton}
                        onClick={() => {
                            if (disabled) return;
                            setLiveSizes(null);
                            setDrag(null);
                            onResetDefaults();
                        }}
                        disabled={disabled}
                    >
                        {t('group.reset_card_layout')}
                    </button>
                )}
            </div>
            <div
                ref={cardRef}
                className={`${styles.card} ${drag ? styles.cardDragging : ''}`}
                style={{ ...bgStyle, aspectRatio: String(CARD_ASPECT) }}
            >
                {CORNERS.map((corner) => {
                    const highlighted = drag?.mode === 'move' && hoverCorner === corner;
                    return (
                        <div
                            key={corner}
                            className={`${styles.cornerHit} ${
                                highlighted ? styles.cornerSelected : ''
                            } ${drag?.mode === 'move' ? styles.cornerVisible : ''}`}
                            style={{
                                ...dropZoneStyle,
                                ...(dragDropInsets
                                    ? cornerPositionStyle(
                                          corner,
                                          dragDropInsets.x,
                                          dragDropInsets.y
                                      )
                                    : null),
                            }}
                            aria-hidden
                        />
                    );
                })}

                {showTitle && (
                    <div
                        className={`${styles.titlePreview} ${
                            drag?.mode === 'move' && drag?.type === 'title'
                                ? `${styles.dragging} ${styles.draggingTextBox}`
                                : ''
                        } ${
                            titleAlignCorner === 'top_right' ||
                            titleAlignCorner === 'bottom_right'
                                ? styles.alignRight
                                : ''
                        }`}
                        style={{
                            color: textColor,
                            fontSize: `${titleCqi}cqi`,
                            lineHeight: 1.25,
                            ...(drag?.mode === 'move' && drag?.type === 'title'
                                ? moveStyle
                                : cornerPositionStyle(
                                      liveTitleLocation,
                                      insetsForTitle(sizes.title).x,
                                      insetsForTitle(sizes.title).y
                                  )),
                        }}
                        onPointerDown={(e) => startMove('title', e)}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-label={t('group.place_card_heading')}
                    >
                        <span className={styles.titleHeading}>{heading}</span>
                        {subtitle ? (
                            <span
                                className={styles.titleSubtitle}
                                style={{ fontSize: `${subtitleCqi}cqi` }}
                            >
                                {subtitle}
                            </span>
                        ) : null}
                        {resizeHandle('title', titleAlignCorner)}
                    </div>
                )}

                <div
                    className={`${styles.textPreview} ${
                        drag?.mode === 'move' && drag?.type === 'text'
                            ? `${styles.dragging} ${styles.draggingTextBox}`
                            : ''
                    } ${
                        textAlignCorner === 'top_right' || textAlignCorner === 'bottom_right'
                            ? styles.alignRight
                            : ''
                    }`}
                    style={{
                        color: textColor,
                        width: '65%',
                        ...(drag?.mode === 'move' && drag?.type === 'text'
                            ? moveStyle
                            : cornerPositionStyle(
                                  liveTextLocation,
                                  insetsForMemberText().x,
                                  insetsForMemberText().y
                              )),
                    }}
                    onPointerDown={(e) => startMove('text', e)}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-label={t('group.place_member_text')}
                >
                    <div className={styles.memberName} style={{ fontSize: `${MEMBER_TEXT_SIZE}cqi` }}>
                        {t('group.preview_member_name')}
                    </div>
                    <div
                        className={styles.memberKennitala}
                        style={{ fontSize: `${MEMBER_TEXT_SIZE}cqi` }}
                    >
                        {t('group.preview_member_kennitala')}
                    </div>
                </div>

                {showLogo && (
                    <div
                        className={`${styles.logoPreview} ${
                            drag?.mode === 'move' && drag?.type === 'logo' ? styles.dragging : ''
                        }`}
                        style={{
                            width: `${sizes.logo}%`,
                            ...(drag?.mode === 'move' && drag?.type === 'logo'
                                ? moveStyle
                                : cornerPositionStyle(
                                      liveLogoLocation,
                                      MEDIA_INSET_X_CQI,
                                      MEDIA_INSET_Y_CQI
                                  )),
                        }}
                        onPointerDown={(e) => startMove('logo', e)}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-label={t('group.place_card_logo')}
                    >
                        {logo ? (
                            <img src={logo} alt="" className={styles.logoImage} />
                        ) : (
                            <div className={styles.logoPlaceholder} />
                        )}
                        {resizeHandle('logo', liveLogoLocation)}
                    </div>
                )}

                {showPhoto && (
                    <div
                        className={`${styles.photoPreview} ${
                            drag?.mode === 'move' && drag?.type === 'photo' ? styles.dragging : ''
                        }`}
                        style={{
                            width: `${sizes.photo * (7 / 9)}cqi`,
                            height: `${sizes.photo}cqi`,
                            ...(drag?.mode === 'move' && drag?.type === 'photo'
                                ? moveStyle
                                : cornerPositionStyle(
                                      livePhotoLocation,
                                      MEDIA_INSET_X_CQI,
                                      MEDIA_INSET_Y_CQI
                                  )),
                        }}
                        onPointerDown={(e) => startMove('photo', e)}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-label={t('group.place_member_photo')}
                    >
                        <div className={styles.photoPlaceholder} />
                        {resizeHandle('photo', livePhotoLocation)}
                    </div>
                )}
            </div>
        </div>
    );
}
